/**
 * DY Nutrition – Custom product page scripts
 */
(function () {
  'use strict';

  const getCurrencyCode = () => {
    if (window.dyCurrency && window.dyCurrency.code) return window.dyCurrency.code;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  };

  const formatMoney = (cents) => {
    const amount = (cents || 0) / 100;
    const code = getCurrencyCode();
    try {
      const locale = document.documentElement.lang || undefined;
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + code;
    }
  };

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* Remembers the flavour/variant a shopper picked per product (keyed by
   * product handle) so it survives Goal switches, which re-render the stack. */
  const dyStackVariantMemory = {};

  /* ------------------------------------------------------------------
   * Subscription / selling-plan helpers
   * ------------------------------------------------------------------ */
  function getVariantsForForm(form) {
    let variants = [];
    const scriptId = form.dataset.variantsScript;
    if (scriptId) {
      const scriptEl = document.getElementById(scriptId);
      if (scriptEl) { try { variants = JSON.parse(scriptEl.textContent); } catch (e) {} }
    } else if (form.dataset.variants) {
      try { variants = JSON.parse(form.dataset.variants); } catch (e) {}
    }
    return variants;
  }

  function getCurrentVariant(form) {
    const variants = getVariantsForForm(form);
    if (!variants.length) return null;
    const idInput = form.querySelector('input[name="id"]');
    const id = idInput ? idInput.value : null;
    if (id) {
      const found = variants.find((v) => String(v.id) === String(id));
      if (found) return found;
    }
    return variants[0] || null;
  }

  function getServingsMap(form) {
    if (form.__dyServingsMap) return form.__dyServingsMap;
    let map = {};
    const id = form.dataset.servingsScript;
    if (id) {
      const el = document.getElementById(id);
      if (el) { try { map = JSON.parse(el.textContent); } catch (e) {} }
    }
    form.__dyServingsMap = map;
    return map;
  }

  function getServings(form, variantId) {
    const map = getServingsMap(form);
    return parseInt(map[variantId], 10) || 0;
  }

  // The currently selected selling plan id: prefer the chosen delivery
  // interval, otherwise fall back to the subscription radio's default plan.
  function getSelectedSellingPlanId(form) {
    const intervalInput = form.querySelector('[data-dy-interval-input]');
    if (intervalInput && intervalInput.value) return String(intervalInput.value);
    const subRadio = form.querySelector(
      '[data-purchase-option="subscription"] input[name="purchase_option"]'
    );
    if (subRadio && subRadio.dataset.sellingPlanId) return String(subRadio.dataset.sellingPlanId);
    return '';
  }

  // Recompute the Subscribe & Save card (price, per-serving, compare line)
  // for the current variant + selected delivery interval. Keeps the
  // subscription radio's data-* values and the hidden selling_plan input in
  // sync so the correct plan is added to cart.
  function updateSubscriptionOption(form) {
    const opt = form.querySelector('[data-purchase-option="subscription"]');
    if (!opt) return;
    const variant = getCurrentVariant(form);
    if (!variant) return;

    const planId = getSelectedSellingPlanId(form);
    let price = variant.price;
    let comparePrice = variant.compare_at_price || 0;

    if (planId && Array.isArray(variant.selling_plan_allocations)) {
      const alloc = variant.selling_plan_allocations.find(
        (a) => String(a.selling_plan_id) === String(planId)
      );
      if (alloc) {
        price = alloc.price;
        if (alloc.compare_at_price && alloc.compare_at_price > comparePrice) {
          comparePrice = alloc.compare_at_price;
        }
      }
    }

    const servings = getServings(form, variant.id);

    const radio = opt.querySelector('input[name="purchase_option"]');
    if (radio) {
      radio.dataset.price = price;
      radio.dataset.comparePrice = comparePrice;
      radio.dataset.sellingPlanId = planId;
    }

    const priceEl = $('[data-option-price]', opt);
    if (priceEl) priceEl.textContent = formatMoney(price);

    const servingsEl = $('[data-option-servings]', opt);
    if (servingsEl) {
      if (servings > 0) {
        servingsEl.textContent = servings + ' servings';
        servingsEl.hidden = false;
      } else {
        servingsEl.hidden = true;
      }
    }

    const perServingEl = $('[data-option-per-serving]', opt);
    const sepEl = $('[data-option-sep]', opt);
    if (perServingEl) {
      if (servings > 0) {
        perServingEl.textContent = formatMoney(Math.round(price / servings)) + ' per serving';
        perServingEl.hidden = false;
        if (sepEl) sepEl.hidden = false;
      } else {
        perServingEl.hidden = true;
        if (sepEl) sepEl.hidden = true;
      }
    }

    const compareLine = $('[data-option-compare-line]', opt);
    const compareEl = $('[data-option-compare]', opt);
    const comparePerEl = $('[data-option-compare-per-serving]', opt);
    if (compareLine) {
      if (comparePrice > price) {
        compareLine.hidden = false;
        if (compareEl) compareEl.textContent = formatMoney(comparePrice);
        if (comparePerEl) {
          if (servings > 0) {
            comparePerEl.textContent =
              '| ' + formatMoney(Math.round(comparePrice / servings)) + ' per serving';
            comparePerEl.hidden = false;
          } else {
            comparePerEl.hidden = true;
          }
        }
      } else {
        compareLine.hidden = true;
      }
    }

    const sellingPlanInput = form.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput && radio && radio.checked) {
      sellingPlanInput.value = planId;
    }
  }

  /* ------------------------------------------------------------------
   * Hero price row updater — purchase-option aware.
   * Reads the currently checked purchase_option radio's data-* values
   * and updates the main .dy-price-row (price, compare, discount badge,
   * data-on-sale attribute for red styling).
   * ------------------------------------------------------------------ */
  function updateHeroPriceFromPurchaseOption(form) {
    const wrapper = form.closest('[data-dy-product-form]') || form;
    const priceRow = wrapper.querySelector('.dy-price-row');
    const priceCurrent = wrapper.querySelector('[data-dy-price-current]');
    const priceCompare = wrapper.querySelector('[data-dy-price-compare]');
    const priceDiscount = wrapper.querySelector('[data-dy-price-discount]');
    if (!priceRow || !priceCurrent) return;

    const checked = form.querySelector('input[name="purchase_option"]:checked');
    if (!checked) return;

    const price = parseInt(checked.dataset.price, 10) || 0;
    const comparePrice = parseInt(checked.dataset.comparePrice, 10) || 0;
    const isOnSale = comparePrice > 0 && comparePrice > price;

    priceCurrent.textContent = formatMoney(price);
    priceRow.setAttribute('data-on-sale', isOnSale ? 'true' : 'false');

    if (priceCompare) {
      if (isOnSale) {
        priceCompare.textContent = formatMoney(comparePrice);
        priceCompare.hidden = false;
      } else {
        priceCompare.hidden = true;
      }
    }
    if (priceDiscount) {
      if (isOnSale) {
        const pct = Math.round(((comparePrice - price) * 100) / comparePrice);
        priceDiscount.textContent = pct + '% OFF TODAY';
        priceDiscount.hidden = false;
      } else {
        priceDiscount.hidden = true;
      }
    }
  }

  function initPurchaseOptions(form) {
    const radios = $$('input[name="purchase_option"]', form);
    const sellingPlanInput = $('input[name="selling_plan"]', form);
    const atc = $('[data-dy-atc]', form);

    if (!radios.length) return;

    const update = () => {
      const checked = radios.find((r) => r.checked);
      if (!checked) return;

      const isSub = !!checked.closest('[data-purchase-option="subscription"]');
      const ctaLabel = checked.dataset.ctaLabel;

      if (isSub) {
        // Recompute price for the selected delivery interval and sync the
        // selling_plan input (updateSubscriptionOption handles both).
        updateSubscriptionOption(form);
      } else if (sellingPlanInput) {
        sellingPlanInput.value = checked.dataset.sellingPlanId || '';
      }
      if (atc && ctaLabel) atc.textContent = ctaLabel;

      updateHeroPriceFromPurchaseOption(form);
    };

    radios.forEach((r) => r.addEventListener('change', update));
    update();

    const intervalDropdown = $('[data-dy-interval-dropdown]', form);
    if (intervalDropdown) {
      const intervalInput = $('[data-dy-interval-input]', form);
      const intervalLabel = $('[data-dy-interval-label]', form);
      const intervalOptions = $$('[data-dy-interval-option]', form);

      intervalOptions.forEach((opt) => {
        opt.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          const value = opt.dataset.value;
          const label = opt.dataset.label;
          if (intervalInput) intervalInput.value = value;
          if (intervalLabel) intervalLabel.textContent = label;
          intervalOptions.forEach((o) => {
            const isSel = o === opt;
            o.classList.toggle('is-selected', isSel);
            o.setAttribute('aria-selected', isSel ? 'true' : 'false');
          });
          // Recompute the Subscribe & Save price + hero badge for the new interval.
          updateSubscriptionOption(form);
          updateHeroPriceFromPurchaseOption(form);
          intervalDropdown.open = false;
        });
      });

      document.addEventListener('click', (e) => {
        if (!intervalDropdown.open) return;
        if (!intervalDropdown.contains(e.target)) intervalDropdown.open = false;
      });
    }
  }

  function initQuantity(form) {
    const qty = $('[data-dy-qty]', form);
    if (!qty) return;
    const input = $('[data-dy-qty-input]', qty);
    const minus = $('[data-dy-qty-decrement]', qty);
    const plus = $('[data-dy-qty-increment]', qty);
    if (!input) return;

    const min = parseInt(input.min, 10) || 1;

    const setValue = (val) => {
      let v = parseInt(val, 10);
      if (isNaN(v) || v < min) v = min;
      input.value = v;
    };

    if (minus) {
      minus.addEventListener('click', (e) => {
        e.preventDefault();
        setValue(parseInt(input.value, 10) - 1);
      });
    }
    if (plus) {
      plus.addEventListener('click', (e) => {
        e.preventDefault();
        setValue(parseInt(input.value, 10) + 1);
      });
    }
    input.addEventListener('change', () => setValue(input.value));
    input.addEventListener('blur', () => setValue(input.value));
  }

  function initVariantPicker(form) {
    let variants = [];
    const scriptId = form.dataset.variantsScript;
    if (scriptId) {
      const scriptEl = document.getElementById(scriptId);
      if (scriptEl) {
        try { variants = JSON.parse(scriptEl.textContent); } catch (e) { return; }
      }
    } else if (form.dataset.variants) {
      try { variants = JSON.parse(form.dataset.variants); } catch (e) { return; }
    }
    if (!variants.length) return;

    const groups = $$('.dy-flavour-dropdown', form).map((dd) => ({
      el: dd,
      radios: $$('input[type="radio"]', dd),
      triggerLabel: $('[data-flavour-current]', dd),
      triggerImg: $('.dy-flavour-trigger__img', dd),
    }));
    if (!groups.length) return;

    const idInput = $('input[name="id"]', form);
    const atc = $('[data-dy-atc]', form);
    const stockPills = $$('[data-dy-stock]', form);

    const findVariant = (selectedValues) => variants.find((v) => (
      Array.isArray(v.options) &&
      v.options.length === selectedValues.length &&
      v.options.every((val, i) => String(val) === String(selectedValues[i]))
    ));

    const update = () => {
      const selectedValues = groups.map((g) => {
        const checked = g.radios.find((r) => r.checked);
        return checked ? checked.value : null;
      });
      if (selectedValues.some((v) => v === null)) return;

      const variant = findVariant(selectedValues);
      if (!variant) return;

      groups.forEach((g) => {
        const checked = g.radios.find((r) => r.checked);
        if (!checked) return;

        if (g.triggerLabel) {
          g.triggerLabel.textContent = checked.dataset.variantTitle || checked.value;
        }
        if (g.triggerImg) {
          const customImgSrc = checked.dataset.variantImage;
          if (customImgSrc) {
            if (g.triggerImg.tagName === 'IMG') {
              g.triggerImg.src = customImgSrc;
            } else {
              const img = document.createElement('img');
              img.className = 'dy-flavour-trigger__img';
              img.src = customImgSrc;
              img.width = 40; img.height = 40;
              img.loading = 'lazy';
              img.alt = '';
              g.triggerImg.replaceWith(img);
              g.triggerImg = img;
            }
          }
        }
        if (g.el.open) {
          setTimeout(() => { g.el.open = false; }, 150);
        }
      });

      if (idInput) {
        idInput.value = variant.id;
        document.dispatchEvent(new CustomEvent('dy:variant-changed', {
          detail: { id: variant.id, variant: variant }
        }));
      }

      const servings = getServings(form, variant.id);

      $$('[data-purchase-option]', form).forEach((opt) => {
        const isSub = opt.dataset.purchaseOption === 'subscription';
        // The subscription option is recomputed by updateSubscriptionOption
        // (below) so it honors the currently selected delivery interval.
        if (isSub) return;

        let price = variant.price;
        let comparePrice = variant.compare_at_price || 0;

        const radio = $('input[type="radio"]', opt);
        if (radio) {
          radio.dataset.price = price;
          radio.dataset.comparePrice = comparePrice;
        }
        const priceEl = $('[data-option-price]', opt);
        if (priceEl) priceEl.textContent = formatMoney(price);

        const servingsEl = $('[data-option-servings]', opt);
        if (servingsEl) {
          if (servings > 0) {
            servingsEl.textContent = servings + ' servings';
            servingsEl.hidden = false;
          } else {
            servingsEl.hidden = true;
          }
        }

        const perServingEl = $('[data-option-per-serving]', opt);
        const sepEl = $('[data-option-sep]', opt);
        if (perServingEl) {
          if (servings > 0) {
            const perServingCents = Math.round(price / servings);
            perServingEl.textContent = formatMoney(perServingCents) + ' per serving';
            perServingEl.hidden = false;
            if (sepEl) sepEl.hidden = false;
          } else {
            perServingEl.hidden = true;
            if (sepEl) sepEl.hidden = true;
          }
        }

        const compareLine = $('[data-option-compare-line]', opt);
        const compareEl = $('[data-option-compare]', opt);
        const comparePerEl = $('[data-option-compare-per-serving]', opt);
        if (compareLine) {
          if (comparePrice > price) {
            compareLine.hidden = false;
            if (compareEl) compareEl.textContent = formatMoney(comparePrice);
            if (comparePerEl) {
              if (servings > 0) {
                const perCompareCents = Math.round(comparePrice / servings);
                comparePerEl.textContent = '| ' + formatMoney(perCompareCents) + ' per serving';
                comparePerEl.hidden = false;
              } else {
                comparePerEl.hidden = true;
              }
            }
          } else {
            compareLine.hidden = true;
          }
        } else if (compareEl) {
          if (comparePrice > price) {
            compareEl.textContent = formatMoney(comparePrice);
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      });

      // Recompute the subscription option for the new variant, honoring the
      // selected delivery interval, then refresh the hero price honoring the
      // currently selected purchase option (subscribe vs one-time).
      updateSubscriptionOption(form);
      updateHeroPriceFromPurchaseOption(form);

      if (atc) {
        atc.disabled = !variant.available;
        atc.dataset.label = variant.available ? '' : 'Sold out';
      }
      stockPills.forEach((stockPill) => {
        const available = variant.available;
        stockPill.dataset.available = available ? 'true' : 'false';

        const labelEl = stockPill.querySelector('.dy-stock-pill__label');
        if (labelEl) {
          labelEl.textContent = available ? 'In stock' : 'Sold out';
        }

        const iconEl = stockPill.querySelector('.dy-stock-pill__icon');
        if (iconEl) {
          iconEl.innerHTML = available
            ? '<circle cx="8" cy="8" r="8" fill="#00B67A"/><path d="M4.5 8.2l2.3 2.3 4.7-4.7" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" fill="none"/>'
            : '<circle cx="8" cy="8" r="8" fill="#000"/><path d="M5.5 5.5l5 5M10.5 5.5l-5 5" stroke="#fff" stroke-width="1.8" stroke-linecap="round"/>';
        }
      });
    };

    groups.forEach((g) => {
      g.radios.forEach((r) => r.addEventListener('change', update));
    });
    update();
  }

  function initBundle(bundle) {
    const totalEl = $('[data-bundle-total]', bundle);
    const compareEl = $('[data-bundle-compare]', bundle);
    const savingsEl = $('[data-bundle-savings]', bundle);
    const cta = $('[data-bundle-cta]', bundle);

    const getChecks = () => $$('input[type="checkbox"][data-variant-id]', bundle);

    const updateTotal = () => {
      const checks = getChecks();
      let total = 0;
      let compare = 0;
      let count = 0;
      checks.forEach((c) => {
        if (c.checked) {
          total += parseInt(c.dataset.price, 10) || 0;
          compare += parseInt(c.dataset.comparePrice || c.dataset.price, 10) || 0;
          count += 1;
        }
      });
      if (totalEl) totalEl.textContent = formatMoney(total);
      if (compareEl) {
        if (compare > total) {
          compareEl.textContent = formatMoney(compare);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (savingsEl) {
        const saved = compare - total;
        if (saved > 0) {
          savingsEl.textContent = 'Save ' + formatMoney(saved);
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }
      if (cta) cta.disabled = count === 0;
    };

    const rebindChecks = () => {
      getChecks().forEach((c) => {
        c.removeEventListener('change', updateTotal);
        c.addEventListener('change', updateTotal);
      });
      updateTotal();
    };

    const rebindFlavours = () => {
      $$('[data-flavour-select]', bundle).forEach((sel) => {
        sel.removeEventListener('change', onFlavourChange);
        sel.addEventListener('change', onFlavourChange);
      });
    };

    const onFlavourChange = (e) => {
      const sel = e.target;
      const opt = sel.options[sel.selectedIndex];
      if (!opt) return;
      const item = sel.closest('.dy-stack-item');
      if (!item) return;

      const variantId = opt.value;
      const price = parseInt(opt.dataset.price, 10) || 0;
      const comparePrice = parseInt(opt.dataset.comparePrice, 10) || price;
      const variantImage = opt.dataset.variantImage;

      // Remember this pick so it persists when goals are switched/re-rendered.
      const handle = item.dataset.productHandle;
      if (handle) dyStackVariantMemory[handle] = String(variantId);

      const check = item.querySelector('input[type="checkbox"][data-variant-id]');
      if (check) {
        check.dataset.variantId = variantId;
        check.dataset.price = price;
        check.dataset.comparePrice = comparePrice;
      }

      const priceEl = item.querySelector('[data-item-price]');
      if (priceEl) priceEl.textContent = formatMoney(price);

      const compareEl = item.querySelector('[data-item-compare]');
      if (compareEl) {
        if (comparePrice > price) {
          compareEl.textContent = formatMoney(comparePrice);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      if (variantImage) {
        const img = item.querySelector('[data-item-img]');
        if (img && img.tagName === 'IMG') {
          img.src = variantImage;
        }
      }

      updateTotal();
    };

    bundle.__dyRebindChecks = () => {
      rebindChecks();
      rebindFlavours();
    };

    rebindChecks();
    rebindFlavours();

    if (cta) {
      const originalLabel = cta.textContent;
      let cartSnapshot = new Map();

      const openCartDrawer = async () => {
        const cartModal = document.querySelector('cart-modal');
        if (cartModal) {
          if (typeof cartModal.reloadContent === 'function') {
            await cartModal.reloadContent();
          }
          if (typeof cartModal.show === 'function') {
            cartModal.show();
            return true;
          }
        }
        return false;
      };

      const updateCartBubble = (data) => {
        const cartBubble = document.getElementById('CartBubble');
        if (cartBubble && data && data.sections && data.sections['cart-bubble']) {
          const match = data.sections['cart-bubble'].match(/\d+/);
          if (match) {
            cartBubble.classList.remove('hidden');
            cartBubble.innerHTML = match[0];
          }
        }
      };

      const setMode = (mode) => {
        cta.dataset.dyMode = mode;
        cta.classList.remove('dy-stack__cta--view-cart', 'dy-stack__cta--update-cart');
        if (mode === 'view') {
          cta.textContent = 'VIEW CART';
          cta.classList.add('dy-stack__cta--view-cart');
        } else if (mode === 'update') {
          cta.textContent = 'UPDATE CART';
          cta.classList.add('dy-stack__cta--update-cart');
        } else {
          cta.textContent = originalLabel;
        }
        cta.disabled = false;
      };

      const onCheckChangeAfterAdd = () => {
        if (cta.dataset.dyMode === 'view' || cta.dataset.dyMode === 'update') {
          const currentIds = new Set(
            getChecks().filter((c) => c.checked).map((c) => parseInt(c.dataset.variantId, 10))
          );
          const snapshotIds = new Set(cartSnapshot.keys());
          let same = currentIds.size === snapshotIds.size;
          if (same) {
            snapshotIds.forEach((id) => { if (!currentIds.has(id)) same = false; });
          }
          setMode(same ? 'view' : 'update');
        }
      };

      const bindPersistentCheckListeners = () => {
        getChecks().forEach((c) => {
          c.removeEventListener('change', onCheckChangeAfterAdd);
          c.addEventListener('change', onCheckChangeAfterAdd);
        });
      };

      const refreshSnapshot = async (variantIds) => {
        try {
          const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const cart = await res.json();
          const wanted = new Set(variantIds);
          cartSnapshot = new Map();
          (cart.items || []).forEach((item) => {
            if (wanted.has(item.variant_id)) {
              cartSnapshot.set(item.variant_id, item.key);
            }
          });
        } catch (e) {}
      };

      const addToCart = async (variantId, quantity) => {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity,
            sections: ['cart-bubble'],
            sections_url: `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}variants/${variantId}`,
          }),
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const data = await res.json();
        updateCartBubble(data);
        document.dispatchEvent(new CustomEvent('product:added-to-cart', {
          detail: { id: variantId, quantity: quantity },
        }));
        return data;
      };

      const removeLine = async (lineKey) => {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: lineKey,
            quantity: 0,
            sections: ['cart-bubble'],
          }),
        });
        if (!res.ok) throw new Error('Remove from cart failed');
        const data = await res.json();
        updateCartBubble(data);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return data;
      };

      cta.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();

        const mode = cta.dataset.dyMode || 'add';

        if (mode === 'view') {
          const opened = await openCartDrawer();
          if (!opened) window.location.href = '/cart';
          return;
        }

        if (cta.disabled) return;

        const checked = getChecks().filter((c) => c.checked);
        const checkedIds = checked.map((c) => parseInt(c.dataset.variantId, 10));

        if (mode === 'update') {
          const currentSet = new Set(checkedIds);
          const snapshotSet = new Set(cartSnapshot.keys());

          const toAdd = [...currentSet].filter((id) => !snapshotSet.has(id));
          const toRemove = [...snapshotSet].filter((id) => !currentSet.has(id));

          if (toAdd.length === 0 && toRemove.length === 0) {
            setMode('view');
            return;
          }

          cta.disabled = true;
          cta.textContent = 'Updating...';
          try {
            for (const variantId of toRemove) {
              const lineKey = cartSnapshot.get(variantId);
              if (lineKey) {
                await removeLine(lineKey);
                cartSnapshot.delete(variantId);
              }
            }
            for (const variantId of toAdd) {
              await addToCart(variantId, 1);
            }
            await refreshSnapshot(checkedIds);

            const cartModal = document.querySelector('cart-modal');
            if (cartModal && typeof cartModal.reloadContent === 'function') {
              await cartModal.reloadContent();
            }

            setMode('view');
            bindPersistentCheckListeners();
          } catch (err) {
            cta.textContent = 'Try again';
            setTimeout(() => setMode('update'), 2000);
          }
          return;
        }

        if (!checked.length) return;

        cta.disabled = true;
        cta.textContent = 'Adding...';
        try {
          for (const variantId of checkedIds) {
            await addToCart(variantId, 1);
          }
          await refreshSnapshot(checkedIds);
          setMode('view');
          bindPersistentCheckListeners();
        } catch (err) {
          cta.textContent = 'Try again';
          setTimeout(() => setMode('add'), 2000);
        }
      });
    }
  }

  function initGoal(goal) {
    const radios = $$('input[type="radio"]', goal);
    const banners = $$('[data-goal-banner]', goal);
    const dataEl = $('[data-dy-goals-data]', goal);

    let goalsData = [];
    if (dataEl) {
      try {
        goalsData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.error('[DY] Failed to parse goals data:', e, dataEl.textContent.slice(0, 500));
      }
    }

    const renderStackItem = (p) => {
      if (!p) return '';
      const isAvailable = p.available !== false;

      // Resolve the active variant: remembered pick → default → first.
      const variants = Array.isArray(p.variants) ? p.variants : [];
      let active = null;
      const remembered = dyStackVariantMemory[p.handle];
      if (remembered) active = variants.find((v) => String(v.id) === String(remembered));
      if (!active) active = variants.find((v) => String(v.id) === String(p.default_variant_id || p.variant_id));
      if (!active && variants.length) active = variants[0];

      const activeId = active ? active.id : p.variant_id;
      const activePrice = active ? active.price : p.price;
      const activeCompare = active ? (active.compare_at_price || 0) : (p.compare_at_price || 0);
      const activeImage = (active && active.image) ? active.image : p.image;
      const activeLabel = active ? (active.label || active.title) : p.variant_title;

      const pickedClass = p.picked_for_you ? ' dy-stack-item--picked' : '';
      const soldoutClass = isAvailable ? '' : ' dy-stack-item--soldout';
      const checked = p.picked_for_you && isAvailable ? 'checked' : '';
      const disabledAttr = isAvailable ? '' : 'disabled';
      const pill = p.picked_for_you
        ? '<span class="dy-stack-item__pill">PICKED FOR YOU</span>'
        : '';
      const displayTitle = p.displayed_title || p.title;
      const sub = p.subtitle_override
        || (activeLabel && activeLabel !== 'Default Title' ? activeLabel : '');
      const desc = p.additional_description
        ? `<div class="dy-stack-item__desc">${escapeHtml(p.additional_description)}</div>`
        : '';
      const img = activeImage
        ? `<img class="dy-stack-item__img" data-item-img src="${activeImage}" alt="${escapeHtml(p.title || '')}" loading="lazy" width="56" height="56">`
        : '<span class="dy-stack-item__img" data-item-img></span>';
      const compare = activeCompare && activeCompare > activePrice
        ? `<s class="dy-stack-item__price-compare" data-item-compare>${formatMoney(activeCompare)}</s>`
        : '<s class="dy-stack-item__price-compare" data-item-compare hidden></s>';
      const subHtml = sub ? `<div class="dy-stack-item__sub" data-item-sub>${escapeHtml(sub)}</div>` : '';

      let flavourHtml = '';
      if (variants.length > 1) {
        const opts = variants.map((v) => {
          const sel = String(v.id) === String(activeId) ? ' selected' : '';
          const dis = v.available === false ? ' disabled' : '';
          const soldTxt = v.available === false ? ' (sold out)' : '';
          return `<option value="${v.id}" data-price="${v.price}" data-compare-price="${v.compare_at_price || v.price}" data-variant-title="${escapeHtml(v.title || '')}" data-variant-image="${v.image || ''}"${dis}${sel}>${escapeHtml(v.label || v.title || '')}${soldTxt}</option>`;
        }).join('');
        flavourHtml = `<div class="dy-stack-item__flavour"><select data-flavour-select aria-label="Choose flavour">${opts}</select></div>`;
      }

      return `
        <label class="dy-stack-item${pickedClass}${soldoutClass}" data-product-handle="${escapeHtml(p.handle || '')}">
          <span class="dy-stack-item__check">
            <input
              type="checkbox"
              data-variant-id="${activeId}"
              data-price="${activePrice}"
              data-compare-price="${activeCompare || activePrice}"
              data-picked="${p.picked_for_you ? 'true' : 'false'}"
              ${disabledAttr}
              ${checked}
            >
            <span class="dy-stack-item__check-box"></span>
          </span>
          ${img}
          <div class="dy-stack-item__info">
            <div class="dy-stack-item__name">${escapeHtml(displayTitle)}</div>
            ${pill}
            ${subHtml}
            ${desc}
            ${flavourHtml}
          </div>
          <div class="dy-stack-item__price">
            <span class="dy-stack-item__price-current" data-item-price>${formatMoney(activePrice)}</span>
            ${compare}
          </div>
        </label>
      `;
    };

    const setGoal = (idx) => {
      banners.forEach((b) => {
        b.hidden = parseInt(b.dataset.goalBanner, 10) !== idx;
      });

      const goalData = goalsData[idx];
      if (!goalData) {
        console.warn('[DY] No goal data for index', idx, '— goalsData has', goalsData.length, 'entries');
        return;
      }

      const validProducts = (goalData.products || []).filter(Boolean);

      document.querySelectorAll('[data-dy-goal-stack]').forEach((stack) => {
        const labelEl = stack.querySelector('[data-stack-label]');
        const titleEl = stack.querySelector('[data-stack-title]');
        const subEl = stack.querySelector('[data-stack-subtitle]');
        const itemsEl = stack.querySelector('[data-stack-items]');

        if (labelEl) labelEl.textContent = goalData.stack_label || '';
        if (titleEl) titleEl.textContent = goalData.stack_title || '';
        if (subEl) {
          if (goalData.stack_subtitle) {
            subEl.textContent = goalData.stack_subtitle;
            subEl.hidden = false;
          } else {
            subEl.hidden = true;
          }
        }
        if (itemsEl) {
          itemsEl.innerHTML = validProducts.map(renderStackItem).join('');
        }

        if (typeof stack.__dyRebindChecks === 'function') {
          stack.__dyRebindChecks();
        }
      });
    };

    radios.forEach((r) => {
      r.addEventListener('change', () => {
        if (r.checked) setGoal(parseInt(r.value, 10));
      });
    });
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function initReviewTabs(root) {
    const tabs = $$('.dy-reviews-tab', root);
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        tabs.forEach((t) => t.setAttribute('aria-pressed', 'false'));
        tab.setAttribute('aria-pressed', 'true');
        const filter = tab.dataset.filter;
        if (window.JudgeMe && typeof window.JudgeMe.filterReviews === 'function') {
          window.JudgeMe.filterReviews(filter);
        } else {
          document.dispatchEvent(new CustomEvent('dy:reviews-filter', { detail: { filter } }));
        }
      });
    });
  }

  function initGallery() {
    $$('.dy-media__carousel').forEach((carousel) => {
      const slides = carousel.querySelectorAll('.dy-media__slide');
      if (!slides.length) return;

      const carouselId = carousel.id;
      const sectionId = carouselId.replace('dy-carousel-', '');
      const thumbsContainer = document.getElementById('dy-thumbs-' + sectionId);
      const thumbs = thumbsContainer ? thumbsContainer.querySelectorAll('.dy-media__thumb') : [];

      const prevBtn = document.querySelector(`[data-dy-media-prev="${carouselId}"]`);
      const nextBtn = document.querySelector(`[data-dy-media-next="${carouselId}"]`);

      const setActiveThumb = (idx) => {
        thumbs.forEach((t, i) => {
          t.setAttribute('aria-pressed', i === idx ? 'true' : 'false');
        });
      };

      const updateArrows = () => {
        const atStart = carousel.scrollLeft <= 4;
        if (prevBtn) prevBtn.hidden = atStart;
        if (nextBtn) nextBtn.hidden = false;
      };

      const observer = new IntersectionObserver(
        (entries) => {
          let best = null;
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
              if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
            }
          });
          if (best) {
            const idx = parseInt(best.target.dataset.slideIndex, 10);
            if (!isNaN(idx)) setActiveThumb(idx);
          }
        },
        { root: carousel, threshold: [0.55, 0.85] }
      );
      slides.forEach((s) => observer.observe(s));

      carousel.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const w = carousel.clientWidth;
          const atEnd = carousel.scrollLeft + w >= carousel.scrollWidth - 4;
          if (atEnd) {
            carousel.scrollTo({ left: 0, behavior: 'smooth' });
          } else {
            carousel.scrollBy({ left: w, behavior: 'smooth' });
          }
        });
      }
      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const w = carousel.clientWidth;
          const atStart = carousel.scrollLeft <= 4;
          if (atStart) {
            carousel.scrollTo({ left: carousel.scrollWidth, behavior: 'smooth' });
          } else {
            carousel.scrollBy({ left: -w, behavior: 'smooth' });
          }
        });
      }

      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const idx = parseInt(thumb.dataset.thumbIndex, 10);
          if (isNaN(idx)) return;
          const slide = carousel.querySelector('[data-slide-index="' + idx + '"]');
          if (slide) {
            slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
          setActiveThumb(idx);
        });
      });
    });
  }

  function initCartTimer() {
    $$('[data-dy-cart-timer]').forEach((el) => {
      const minutes = parseInt(el.dataset.dyCartHoldMinutes, 10) || 0;
      if (minutes <= 0) return;
      let remaining = minutes * 60;
      const render = () => {
        const mm = Math.floor(remaining / 60);
        const ss = remaining % 60;
        el.textContent = mm + ':' + String(ss).padStart(2, '0');
      };
      render();
      const id = setInterval(() => {
        remaining -= 1;
        if (remaining < 0) { clearInterval(id); el.textContent = '0:00'; return; }
        render();
      }, 1000);
    });
  }

  /* ------------------------------------------------------------------
   * Shopify rich text AST → HTML renderer
   * ------------------------------------------------------------------ */
  function renderRichText(node) {
    if (!node) return '';
    if (typeof node === 'string') {
      try { node = JSON.parse(node); } catch (e) { return escapeHtml(node); }
    }
    if (Array.isArray(node)) {
      return node.map(renderRichText).join('');
    }
    if (typeof node !== 'object') return '';

    const children = node.children ? node.children.map(renderRichText).join('') : '';

    switch (node.type) {
      case 'root':
        return children;
      case 'paragraph':
        if (!children || children.trim() === '') return '<p></p>';
        return `<p>${children}</p>`;
      case 'heading': {
        const level = Math.max(1, Math.min(6, node.level || 2));
        return `<h${level}>${children}</h${level}>`;
      }
      case 'list': {
        const listType = node.listType || node.list_type;
        const tag = listType === 'ordered' ? 'ol' : 'ul';
        return `<${tag}>${children}</${tag}>`;
      }
      case 'list-item':
        return `<li>${children}</li>`;
      case 'link': {
        const url = String(node.url || '#').replace(/"/g, '&quot;');
        const title = node.title ? ` title="${String(node.title).replace(/"/g, '&quot;')}"` : '';
        const target = node.target ? ` target="${String(node.target).replace(/"/g, '&quot;')}"` : '';
        return `<a href="${url}"${title}${target}>${children}</a>`;
      }
      case 'text': {
        let txt = escapeHtml(node.value || '');
        const marks = Array.isArray(node.marks) ? node.marks : [];
        const isBold = node.bold === true || marks.includes('bold') || marks.some(m => m && m.type === 'bold');
        const isItalic = node.italic === true || marks.includes('italic') || marks.some(m => m && m.type === 'italic');
        if (isBold) txt = `<strong>${txt}</strong>`;
        if (isItalic) txt = `<em>${txt}</em>`;
        return txt;
      }
      default:
        return children;
    }
  }

  function initRichText() {
    document.querySelectorAll('[data-dy-richtext]').forEach((el) => {
      if (el.__dyRtRendered) return;
      el.__dyRtRendered = true;

      const scriptEl = el.querySelector('script[type="application/json"]');
      if (!scriptEl) return;
      const raw = scriptEl.textContent.trim();
      if (!raw) return;

      try {
        let parsed = JSON.parse(raw);
        if (typeof parsed === 'string' && parsed.trim().startsWith('{')) {
          parsed = JSON.parse(parsed);
        }
        const html = renderRichText(parsed);
        el.innerHTML = html;
      } catch (e) {
        console.error('[dy-richtext] parse failed:', e);
        el.textContent = raw;
      }
    });
  }

  const once = (el, fn) => {
    if (!el || el.__dyInited) return;
    el.__dyInited = true;
    fn(el);
  };

  function cleanEmptyJudgmeReplies() {
    var replies = document.querySelectorAll('.jdgm-rev__reply');
    if (!replies.length) return;
    replies.forEach(function (reply) {
      var content = reply.querySelector('.jdgm-rev__reply-content');
      var text = content ? content.textContent.trim() : '';
      if (!text) {
        reply.style.display = 'none';
      }
    });
  }

  function watchJudgmeReplies() {
    cleanEmptyJudgmeReplies();
    var widget = document.querySelector('.jdgm-widget');
    if (!widget || !('MutationObserver' in window)) return;
    var observer = new MutationObserver(function () {
      cleanEmptyJudgmeReplies();
    });
    observer.observe(widget, { childList: true, subtree: true });
  }

  function initVisitorCount() {
    const els = document.querySelectorAll('[data-dy-visitor-count]');
    if (!els.length) return;

    const TICK_MS = 8 * 1000;
    const GROW_CHANCE = 0.5;

    function storageKey(el) {
      const base = el.dataset.dyVisitorBase || '0';
      return 'dy_visitor_' + base + '_' + (el.dataset.dyVisitorRange || '0');
    }

    function readStored(el) {
      try {
        const raw = sessionStorage.getItem(storageKey(el));
        return raw ? parseInt(raw, 10) : null;
      } catch (e) {
        return null;
      }
    }

    function writeStored(el, value) {
      try {
        sessionStorage.setItem(storageKey(el), String(value));
      } catch (e) {}
    }

    function setupElement(el) {
      const base = parseInt(el.dataset.dyVisitorBase, 10) || 0;
      const range = parseInt(el.dataset.dyVisitorRange, 10) || 0;
      const cap = base + Math.max(1, range);

      const stored = readStored(el);
      let current = stored && stored >= base ? stored : base;
      el.textContent = current;
      writeStored(el, current);

      function tick() {
        if (current >= cap) return;
        if (Math.random() < GROW_CHANCE) {
          current += 1;
          el.textContent = current;
          writeStored(el, current);
        }
      }
      setInterval(tick, TICK_MS);
    }

    els.forEach(setupElement);
  }

  function initFinalCta() {
    const buttons = document.querySelectorAll('[data-dy-final-cta-button]');
    if (!buttons.length) return;

    let variants = [];
    const variantsScript = document.querySelector('[data-dy-final-cta-variants]');
    if (variantsScript) {
      try {
        variants = JSON.parse(variantsScript.textContent.trim());
      } catch (e) {}
    }

    buttons.forEach((button) => {
      const form = button.closest('form');
      if (!form) return;
      if (form.__dyFinalCtaInited) return;
      form.__dyFinalCtaInited = true;

      const group = form.closest('.dy-final-cta__group') || form;

      const variantInput = form.querySelector('[data-dy-final-cta-variant-id]');
      const priceEl = form.querySelector('[data-dy-final-cta-price]');
      const triggerImg = group.querySelector('[data-dy-final-cta-trigger-img]');
      const triggerLabel = group.querySelector('[data-dy-final-cta-trigger-label]');
      const dropdown = group.querySelector('[data-dy-final-cta-flavour]');
      const flavourRadios = group.querySelectorAll('[data-dy-final-cta-variant-radio]');

      function syncVariant(variantId, opts) {
        if (!variantId) return;
        const v = variants.find((x) => x.id === parseInt(variantId, 10));
        if (!v) return;
        if (variantInput) variantInput.value = v.id;
        if (priceEl) {
          if (typeof formatMoney === 'function' && window.dyCurrency) {
            priceEl.textContent = formatMoney(v.price);
          } else {
            priceEl.textContent = v.price_formatted;
          }
        }
        if (button) {
          button.disabled = !v.available;
        }
        if (opts && opts.fromRadio) {
          const radio = opts.fromRadio;
          if (triggerLabel) {
            const title = radio.dataset.variantTitle;
            if (title) triggerLabel.textContent = title;
          }
          if (triggerImg) {
            const newSrc = radio.dataset.variantImage;
            if (newSrc) {
              if (triggerImg.tagName === 'IMG') {
                triggerImg.src = newSrc;
              } else {
                const img = document.createElement('img');
                img.className = 'dy-flavour-trigger__img';
                img.setAttribute('data-dy-final-cta-trigger-img', '');
                img.src = newSrc;
                img.alt = '';
                img.loading = 'lazy';
                img.width = 40;
                img.height = 40;
                triggerImg.replaceWith(img);
              }
            }
          }
        }
      }

      flavourRadios.forEach((r) => {
        r.addEventListener('change', () => {
          if (!r.checked || r.disabled) return;
          syncVariant(r.value, { fromRadio: r });
          if (dropdown && dropdown.open) dropdown.open = false;
        });
      });

      function externalSync(variantId) {
        if (!variantId) return;
        const match = Array.from(flavourRadios).find(
          (r) => r.value === String(variantId)
        );
        if (match && !match.disabled) {
          if (!match.checked) {
            match.checked = true;
            match.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          syncVariant(variantId);
        }
      }

      document.addEventListener('dy:variant-changed', (e) => {
        if (e.detail && e.detail.id) externalSync(e.detail.id);
      });

      const mainForm = document.querySelector('[data-dy-product-form] form, form[data-dy-product-form]');
      if (mainForm) {
        const idInput = mainForm.querySelector('input[name="id"]');
        if (idInput) {
          new MutationObserver(() => externalSync(idInput.value))
            .observe(idInput, { attributes: true, attributeFilter: ['value'] });
          if (idInput.value && variantInput && idInput.value !== variantInput.value) {
            externalSync(idInput.value);
          }
        }
      }

      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (!variantInput || !variantInput.value) return;
        const id = parseInt(variantInput.value, 10);
        if (!id) return;

        const originalLabel = button.querySelector('.dy-final-cta__button-label');
        const originalLabelText = originalLabel ? originalLabel.textContent : '';

        button.disabled = true;
        if (originalLabel) originalLabel.textContent = 'ADDING...';

        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              id: id,
              quantity: 1,
              sections: ['cart-bubble'],
              sections_url: `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}variants/${id}`,
            }),
          });
          if (!res.ok) throw new Error('Add to cart failed');
          const data = await res.json();

          const cartBubble = document.getElementById('CartBubble');
          if (cartBubble && data.sections && data.sections['cart-bubble']) {
            const match = data.sections['cart-bubble'].match(/\d+/);
            if (match) {
              cartBubble.classList.remove('hidden');
              cartBubble.innerHTML = match[0];
            }
          }

          document.dispatchEvent(new CustomEvent('product:added-to-cart', {
            detail: { id: id, quantity: 1 },
          }));

          const cartModal = document.querySelector('cart-modal');
          if (cartModal) {
            if (typeof cartModal.reloadContent === 'function') cartModal.reloadContent();
            if (typeof cartModal.show === 'function') cartModal.show();
          }

          if (originalLabel) originalLabel.textContent = originalLabelText;
          button.disabled = false;
        } catch (err) {
          if (originalLabel) originalLabel.textContent = originalLabelText;
          button.disabled = false;
        }
      });
    });
  }

  function initBundleTeleport() {
    const bundleSection = document.querySelector(
      '.shopify-section:has(.dy-stack[data-dy-goal-stack]), .shopify-section [data-dy-goal-stack]'
    );
    if (!bundleSection) return;
    const sectionEl =
      bundleSection.closest('.shopify-section') ||
      bundleSection.closest('[id^="shopify-section-"]');
    if (!sectionEl) return;

    const mainMedia = document.querySelector(
      '.shopify-section .dy-product-grid .dy-media, [id^="shopify-section-"] .dy-product-grid .dy-media'
    );
    if (!mainMedia) return;

    const originalParent = sectionEl.parentNode;
    const originalNext = sectionEl.nextSibling;

    const mql = window.matchMedia('(min-width: 990px)');
    const apply = () => {
      if (mql.matches) {
        if (sectionEl.parentNode !== mainMedia) {
          mainMedia.appendChild(sectionEl);
          sectionEl.classList.add('dy-bundle-teleported');
        }
      } else {
        if (sectionEl.parentNode !== originalParent) {
          originalParent.insertBefore(sectionEl, originalNext);
          sectionEl.classList.remove('dy-bundle-teleported');
        }
      }
    };
    apply();
    mql.addEventListener('change', apply);
  }

  /* ------------------------------------------------------------------
   * Main product Add-to-cart — AJAX submit.
   * Prevents the native redirect to /cart. Adds the selected variant
   * (respecting quantity + selling_plan from the form) and opens the
   * cart drawer (cart-modal) instead.
   * ------------------------------------------------------------------ */
  function initMainAtc(container) {
    const formEl = container.querySelector('form');
    if (!formEl) return;
    const atc = $('[data-dy-atc]', formEl) || $('[data-dy-atc]', container);
    if (!atc) return;

    formEl.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (atc.disabled) return;

      const idInput = formEl.querySelector('input[name="id"]');
      if (!idInput || !idInput.value) {
        formEl.submit();
        return;
      }

      const originalLabel = atc.textContent;
      atc.disabled = true;
      atc.textContent = 'Adding...';

      try {
        const formData = new FormData(formEl);
        formData.append('sections', 'cart-bubble');
        formData.append(
          'sections_url',
          (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'
        );

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        if (!res.ok) throw new Error('Add to cart failed');
        const data = await res.json();

        const cartBubble = document.getElementById('CartBubble');
        if (cartBubble && data.sections && data.sections['cart-bubble']) {
          const match = data.sections['cart-bubble'].match(/\d+/);
          if (match) {
            cartBubble.classList.remove('hidden');
            cartBubble.innerHTML = match[0];
          }
        }

        document.dispatchEvent(new CustomEvent('product:added-to-cart', {
          detail: { id: parseInt(idInput.value, 10) },
        }));

        const cartModal = document.querySelector('cart-modal');
        if (cartModal) {
          if (typeof cartModal.reloadContent === 'function') await cartModal.reloadContent();
          if (typeof cartModal.show === 'function') cartModal.show();
        } else {
          window.location.href = '/cart';
          return;
        }

        atc.textContent = originalLabel;
        atc.disabled = false;
      } catch (err) {
        atc.textContent = originalLabel;
        atc.disabled = false;
      }
    });
  }

  function boot() {
    $$('[data-dy-product-form]').forEach((form) => once(form, (f) => {
      initVariantPicker(f);
      initPurchaseOptions(f);
      initQuantity(f);
      initMainAtc(f);
    }));
    $$('[data-dy-bundle]').forEach((b) => once(b, initBundle));
    $$('[data-dy-goal]').forEach((g) => once(g, initGoal));
    $$('[data-dy-reviews]').forEach((r) => once(r, initReviewTabs));
    if (!window.__dyGlobalInited) {
      window.__dyGlobalInited = true;
      initGallery();
      initCartTimer();
      initVisitorCount();
      initFinalCta();
      initBundleTeleport();
      setTimeout(watchJudgmeReplies, 500);
    }
    initRichText();
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);

  document.addEventListener('shopify:section:load', (e) => {
    if (e.target) {
      e.target.__dyInited = false;
      $$('[data-dy-product-form], [data-dy-bundle], [data-dy-goal], [data-dy-reviews]', e.target).forEach((el) => {
        el.__dyInited = false;
      });
    }
    boot();
  });
})();